import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import SwipeableViews from "react-swipeable-views";
import virtualize from "react-swipeable-views-utils/lib/virtualize";
import mod from "react-swipeable-views-core/lib/mod";
import Image from "components/image-containers/Image/Image.component";

import MobileStepper from "@mui/material/MobileStepper";

// Helpers
import { inventoryVehiclePropType } from "custom-prop-types/vehicle-props";
import ImageNavigationNext from "components/buttons/ImageNavigationButtons/ImageNavigationNext.component";
import ImageNavigationPrevious from "components/buttons/ImageNavigationButtons/ImageNavigationPrevious.component";
import { getVehiclePhotosV1 } from "requests/v1/vehicles-api";
import { comingSoon, greyLoader } from "assets/externalImages";

// Styles
import useStyles from "./SwipeableImageContainer.styles";

const EnhancedSwipeableViews = virtualize(SwipeableViews);
const SwipeableImageContainer = (props) => {
  const {
    vehicle,
    mouseEvents,
    variant,
    showButtons,
    width,
    height,
    resistance,
    infinite,
    size,
    carouselIndex,
    contain,
    priority,
    imageClassName,
  } = props;

  const classes = useStyles({ contain, width });
  const [activeStep, setActiveStep] = useState(infinite ? 1 : 0);
  const [morePhotosFetched, setMorePhotosFetched] = useState(false);

  const initialPhotos = ({ photoCount, mainPhoto, photos }) => {
    if (photoCount && mainPhoto?.src && photos)
      return photos.filter((photo) => photo.label !== "vin");
    if (mainPhoto?.src) {
      if (photoCount >= 2 && !morePhotosFetched) {
        return [{ ...mainPhoto }, { id: "grey-loader", src: greyLoader }];
      }
      return [{ ...mainPhoto }];
    }
    return [{ id: "coming-soon", src: comingSoon }];
  };

  const [photos, setPhotos] = useState(initialPhotos(vehicle));
  // If we have no main photo, we assume there are no other photos to fetch
  const maxSteps =
    !morePhotosFetched && vehicle.mainPhoto
      ? vehicle.photoCount
      : photos.length;
  const photoUrlHelper = (photo) => photo.src;

  const fetchMorePhotos = () => {
    if (photos.length !== maxSteps) {
      getVehiclePhotosV1(vehicle.id).then((response) => {
        if (response.status === 200) {
          setPhotos(response.data.data);
          setMorePhotosFetched(true);
        }
      });
    }
  };

  const handleNext = (e) => {
    e.preventDefault();
    e.stopPropagation();
    fetchMorePhotos();
    setActiveStep((prevActiveStep) =>
      prevActiveStep === maxSteps - 1 ? 0 : prevActiveStep + 1,
    );
  };

  const handlePrevious = (e) => {
    e.preventDefault();
    e.stopPropagation();
    fetchMorePhotos();
    setActiveStep((prevActiveStep) =>
      prevActiveStep === 0 ? maxSteps - 1 : prevActiveStep - 1,
    );
  };

  const handleStepChange = (step) => {
    fetchMorePhotos();
    setActiveStep(mod(step, maxSteps));
  };

  const handleRender = (params) => {
    const { index, key } = params;
    const photoIndex = infinite
      ? mod(index, maxSteps)
      : Math.max(Math.min(index, 18), 0);

    return (
      <div className={classes.imgContainer} key={key}>
        <Image
          imageClassName={classes.swipableImage}
          className={clsx(classes.img, imageClassName)}
          fill
          src={photoUrlHelper(photos[photoIndex], { size, width, height })}
          fallback={comingSoon}
          alt={`vehicle ${index}`}
          priority={priority}
        />
      </div>
    );
  };

  const preventPropagation = (e) => {
    e.stopPropagation();
  };

  useEffect(() => {
    setActiveStep(carouselIndex);
  }, [carouselIndex]);

  return (
    <div className={classes.root} onTouchMove={preventPropagation}>
      {infinite ? (
        <EnhancedSwipeableViews
          resistance={resistance}
          index={activeStep}
          onChangeIndex={handleStepChange}
          enableMouseEvents={mouseEvents}
          slideRenderer={handleRender}
          overscanSlideAfter={maxSteps}
          overscanSlideBefore={maxSteps}
        />
      ) : (
        <SwipeableViews
          resistance={resistance}
          index={activeStep}
          onChangeIndex={handleStepChange}
          enableMouseEvents={mouseEvents}
        >
          {photos.map((photo, index) => (
            <div
              className={clsx(classes.imgContainer, imageClassName)}
              key={photo.id}
            >
              {Math.abs(activeStep - index) <= 2 ? (
                <Image
                  className={classes.img}
                  width={300}
                  height={200}
                  fallback={comingSoon}
                  src={photo.src}
                  alt={`vehicle ${index}`}
                  priority={priority}
                />
              ) : null}
            </div>
          ))}
        </SwipeableViews>
      )}

      {showButtons && maxSteps > 1 && (
        <ImageNavigationPrevious
          handleClick={handlePrevious}
          index={vehicle.id}
        />
      )}
      {showButtons && maxSteps > 1 && (
        <ImageNavigationNext handleClick={handleNext} index={vehicle.id} />
      )}
      {["dots", "text", "progress"].includes(variant) && maxSteps > 1 && (
        <MobileStepper
          className={clsx(
            classes.dotsContainer,
            variant === "text" && classes.textVariant,
          )}
          steps={maxSteps}
          variant={variant}
          activeStep={activeStep}
        />
      )}
    </div>
  );
};

SwipeableImageContainer.propTypes = {
  vehicle: inventoryVehiclePropType.isRequired,
  mouseEvents: PropTypes.bool,
  showButtons: PropTypes.bool,
  variant: PropTypes.string,
  resistance: PropTypes.bool,
  width: PropTypes.number,
  height: PropTypes.number,
  infinite: PropTypes.bool,
  size: PropTypes.string,
  carouselIndex: PropTypes.number,
  style: PropTypes.shape({}),
  contain: PropTypes.bool,
  priority: PropTypes.bool,
};

SwipeableImageContainer.defaultProps = {
  mouseEvents: true,
  showButtons: false,
  variant: "none",
  resistance: false,
  width: 250,
  height: 250,
  infinite: false,
  size: "small",
  carouselIndex: 0,
  style: {},
  contain: false,
  priority: false,
};

export default SwipeableImageContainer;
